import type { FieldSyncCache } from '@/mobile/sync/fieldSyncCache';

/** Rotas obrigatórias para login offline concluir (menu + cadastros de relatório). */
export const MOBILE_OFFLINE_REQUIRED_PATHS: readonly string[] = [
  '/dashboard',
  '/api/mobile/dashboard',
  '/field/reports',
  '/field/projects',
  '/field/agenda',
  '/field/rdo/create',
  '/field/re/create',
  '/field/rc/create',
  '/field/rt/create',
  '/field/rcp/create',
  '/field/rcp/approvals',
  '/settings/profile',
  '/api/mobile/settings/profile',
];

const FIELD_REPORT_EDIT_PATH_BY_TYPE: Record<string, string> = {
  rdo: '/field/rdo/edit',
  re: '/field/re/edit',
  rc: '/field/rc/edit',
  rt: '/field/rt/edit',
  rcp: '/field/rcp/edit',
  rcp_team: '/field/rcp/team',
};

const FIELD_REPORT_CREATE_PATH_BY_TYPE: Record<string, string> = {
  rdo: '/field/rdo/create',
  re: '/field/re/create',
  rc: '/field/rc/create',
  rt: '/field/rt/create',
  rcp: '/field/rcp/create',
};

/** Opcionais (ex.: exigem verified no servidor); tentamos baixar, mas não bloqueiam login. */
export const MOBILE_OFFLINE_OPTIONAL_PATHS: readonly string[] = [
  '/settings/password',
  '/settings/two-factor',
  '/settings/appearance',
  '/settings/language',
  // Gestão/aprovação de relatórios: visível offline para gestores.
  // Aprovar/reprovar continuam exigindo internet (POST direto, sem fila).
  '/admin/field-management',
];

/** Todas as rotas estáticas para prefetch. */
export const MOBILE_STATIC_PATHS: readonly string[] = [
  ...MOBILE_OFFLINE_REQUIRED_PATHS,
  ...MOBILE_OFFLINE_OPTIONAL_PATHS,
];

const MAX_REPORT_PREFETCH = 60;
const MAX_PROJECT_PREFETCH = 40;
const MAX_CREATE_BY_PROJECT = 25;

export type MobilePrefetchLimits = {
  maxReports: number;
  maxProjects: number;
  maxCreateByProject: number;
};

/** Limites padrão para prefetch em background (login não bloqueia nisso). */
export const DEFAULT_MOBILE_PREFETCH_LIMITS: MobilePrefetchLimits = {
  maxReports: MAX_REPORT_PREFETCH,
  maxProjects: MAX_PROJECT_PREFETCH,
  maxCreateByProject: MAX_CREATE_BY_PROJECT,
};

/** @deprecated Prefira DEFAULT_MOBILE_PREFETCH_LIMITS; evita milhares de telas no login. */
export const OFFLINE_PACKAGE_LIMITS: MobilePrefetchLimits = {
  maxReports: 2000,
  maxProjects: 500,
  maxCreateByProject: 500,
};

export function isMobileOfflinePath(pathname: string): boolean {
  if (pathname === '/dashboard' || pathname === '/dashboard/') {
    return true;
  }

  if (pathname.startsWith('/field/')) {
    return true;
  }

  if (pathname.startsWith('/settings/')) {
    return true;
  }

  if (pathname === '/admin/field-management' || pathname === '/admin/field-management/') {
    return true;
  }

  return false;
}

/**
 * URLs Inertia derivadas do cache de sync (relatórios, edição, cadastros por obra, cronograma).
 */
export function dynamicPathsFromFieldCache(
  cache: FieldSyncCache | null,
  limits: MobilePrefetchLimits = DEFAULT_MOBILE_PREFETCH_LIMITS,
): string[] {
  if (!cache) {
    return [];
  }

  const paths = new Set<string>();

  for (const report of cache.reports.slice(0, limits.maxReports)) {
    if (!report.id) {
      continue;
    }

    paths.add(`/field/reports/${report.id}`);

    if (report.canEdit && report.type) {
      const editBase = FIELD_REPORT_EDIT_PATH_BY_TYPE[report.type];
      if (editBase) {
        paths.add(`${editBase}/${report.id}`);
      }
    }
  }

  for (const project of cache.projects.slice(0, limits.maxProjects)) {
    if (!project.id) {
      continue;
    }

    paths.add(`/field/rdo/schedule-tree/${project.id}`);
  }

  for (const project of cache.projects.slice(0, limits.maxCreateByProject)) {
    if (!project.id) {
      continue;
    }

    for (const createPath of Object.values(FIELD_REPORT_CREATE_PATH_BY_TYPE)) {
      paths.add(`${createPath}?project_id=${project.id}`);
    }
  }

  return [...paths];
}

export function collectAllMobilePrefetchPaths(
  cache: FieldSyncCache | null,
  options?: { limits?: MobilePrefetchLimits },
): string[] {
  const limits = options?.limits ?? DEFAULT_MOBILE_PREFETCH_LIMITS;
  const merged = new Set<string>([
    ...MOBILE_STATIC_PATHS,
    ...dynamicPathsFromFieldCache(cache, limits),
  ]);

  return [...merged];
}

/** Rotas extras para prefetch em background (fora do menu mínimo). */
export function collectBackgroundMobilePrefetchPaths(
  cache: FieldSyncCache | null,
  limits: MobilePrefetchLimits = DEFAULT_MOBILE_PREFETCH_LIMITS,
): string[] {
  const required = new Set<string>(MOBILE_OFFLINE_REQUIRED_PATHS);
  const all = collectAllMobilePrefetchPaths(cache, { limits });

  return all.filter((path) => !required.has(path));
}
